import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import APIEndPoints from '../../../../middleware/APIEndPoints';

const ProjectImage = () => {
    const { projectId } = useParams(); // Get the project ID from URL
    const [images, setImages] = useState([]); // State to store fetched images
    const [imageData, setImageData] = useState({
        title: '',
        imageFile: null
    });
    const [imagePreview, setImagePreview] = useState(null); // For previewing the image before upload

    const baseURL = APIEndPoints.myprojects.url;
    const cloudName = process.env.REACT_APP_CLOUD_NAME;
    const uploadPreset = 'upload'; // Cloudinary upload preset

    // Fetch project images when the component loads
    useEffect(() => {
        const fetchImages = async () => {
            try {
                const response = await axios.get(`${baseURL}/${projectId}/images`);
                setImages(response.data); // Set the images in state
            } catch (error) {
                console.error('Error fetching images:', error);
            }
        };

        fetchImages();
    }, [projectId]);

    // Handle file and input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setImageData({ ...imageData, [name]: value });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setImageData({ ...imageData, imageFile: file });
        // Generate a preview of the image
        if (file) {
            const previewURL = URL.createObjectURL(file);
            setImagePreview(previewURL);
        }
    };

    // Function to upload image to Cloudinary
    const handleUpload = async () => {
        const formData = new FormData();
        formData.append('file', imageData.imageFile);
        formData.append('upload_preset', uploadPreset);

        try {
            // Upload image to Cloudinary
            const response = await axios.post(
                `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
                formData
            );
            return response.data.secure_url; // Return the uploaded image URL
        } catch (error) {
            console.error('Error uploading image:', error);
            return null;
        }
    };

    // Handle image submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!imageData.imageFile) {
            alert('Please select an image to upload.');
            return;
        }

        // Upload image to Cloudinary
        const imageUrl = await handleUpload();
        if (!imageUrl) {
            alert('Failed to upload image.');
            return;
        }

        try {
            // Send image URL and title to the backend
            const response = await axios.post(`${baseURL}/${projectId}/images`, {
                title: imageData.title,
                url: imageUrl
            });

            // Update the images array
            setImages([...images, response.data]);
            setImageData({ title: '', imageFile: null }); // Reset the form
            setImagePreview(null); // Clear image preview
            console.log("Successfully added the image");
        } catch (error) {
            console.error('Error uploading image:', error);
        }
    };


    // Handle image deletion
    const handleDelete = async (imageId) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this image?');
        if (!confirmDelete) return;

        try {
            // Send a delete request to the backend
            await axios.delete(`${baseURL}/${projectId}/images/${imageId}`);
            // Update the images array by removing the deleted image
            setImages(images.filter((image) => image._id !== imageId));
            console.log("Successfully deleted the image");
        } catch (error) {
            console.error('Error deleting image:', error);
        }
    };


    return (
        <div className="p-6">
            <h2 className="text-2xl font-bold mb-4">Manage Project Images</h2>

            {/* Image input fields outside of the form */}
            <div className="mb-6">
                <div className="mb-4">
                    <label htmlFor="title" className="block text-white">Image Title:</label>
                    <input
                        type="text"
                        id="title"
                        name="title"
                        value={imageData.title}
                        onChange={handleInputChange}
                        className="w-full p-2 mt-2 rounded bg-gray-800 text-white"
                        required
                    />
                </div>
                <div className="mb-4">
                    <label htmlFor="imageFile" className="block text-white">Upload Image:</label>
                    <input
                        type="file"
                        id="imageFile"
                        name="imageFile"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="w-full p-2 mt-2 rounded bg-gray-800 text-white"
                        required
                    />
                </div>

                {/* Image Preview */}
                {imagePreview && (
                    <div className="mb-4">
                        <h3 className="text-white">Image Preview:</h3>
                        <img src={imagePreview} alt="Image Preview" className="w-full h-auto rounded mt-2" />
                    </div>
                )}

                <button onClick={handleSubmit} className="bg-blue-600 px-4 py-2 rounded text-white hover:bg-blue-700">
                    Upload Image
                </button>
            </div>

            {/* Display all uploaded images */}
            <h3 className="text-xl font-bold mb-4">Existing Images</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {images.length > 0 ? (
                    images.map((image, index) => (
                        <div key={image._id || index} className="bg-gray-800 p-4 rounded-lg">
                            <img src={image.url} alt={image.title} className="w-full h-auto rounded" />
                            <p className="text-white mt-2">{image.title}</p>
                            <button
                                onClick={() => handleDelete(image._id)}
                                className="bg-red-600 px-2 py-1 rounded text-white hover:bg-red-700 mt-2"
                            >
                                Delete
                            </button>
                        </div>
                    ))
                ) : (
                    <p className="text-white">No images uploaded for this project yet.</p>
                )}
            </div>
        </div>
    );
};

export default ProjectImage;
